import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import {
  VAIR_PLAYER_SEARCH_API,
  VairPlayerSearchResponse,
  VairPlayersPayload,
  VairUser,
} from 'constants/payloads/vair/playerSearch';
import {
  EventGroupFormatsEnum,
  EventInvitationStatusesEnum,
  PaymentFulfillmentChannelsEnum,
  SportsEnum,
  useGetEventInvitationForRegistrationLazyQuery,
  useGetEventTeamInvitationByIdLazyQuery,
  useGetUserEventRegistrationLazyQuery,
  useUpdateUserDefaultCoachPaymentFulfillmentChannelMutation,
} from 'types/generated/client';
import { trackEvent } from 'services/client/analytics';
import { getEngagementSourceFromUrl, trackCtaClick } from 'services/client/analytics/engagement';
import {
  TOURNAMENT_CLICK_REGISTER_EXTERNAL,
  TOURNAMENT_NAVIGATE_EXTERNAL,
} from 'services/client/analytics/events';
import { Response, SETUP_INTENT_API, getSetupIntent } from 'services/client/stripe/getSetupIntent';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { calculateEventOrderTotal } from 'utils/shared/money/calculateEventOrderTotal';
import { parseVairSportRating } from 'utils/shared/vair/parsePrimaryRating';
import { getIsVairified } from 'utils/shared/vair/vairSportStatus';
import { useApiGateway } from 'hooks/useApi';
import { useAuthModals } from 'hooks/useAuthModals';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useStripe } from 'hooks/useStripe';
import { useVairConnection } from 'hooks/useVairConnection';
import { useVairPlayerSearch } from 'hooks/useVairPlayerSearch';
import { useViewer } from 'hooks/useViewer';
import ArrowRight from 'svg/ArrowRight';
import CloseIcon from 'svg/CloseIcon';
import BankAccountPrompt from 'screens/CoachDetails/BankAccountPrompt';
import { SELF_RATE_SKILL_LEVELS } from 'screens/FindOnboarding/types';
import { Button, ButtonText } from 'components/Button';
import Modal, { useModal } from 'components/modals/Modal';
import ModalStripeOnboarding from 'components/modals/ModalStripeOnboarding';
import { ForNavigationType } from 'components/modals/ModalStripeOnboarding/types';
import AddEvents from './AddEvents';
import Canceled from './Canceled';
import Closed from './Closed';
import CreateAccount from './CreateAccount';
import LoadingSkeleton from './LoadingSkeleton';
import Login from './Login';
import ModalAdditionalDetails from './ModalAdditionalDetails';
import ModalVairInfo from './ModalVairInfo';
import PartnerSetup from './PartnerSetup';
import { TournamentPaymentChoice } from './PaymentMethodChoice';
import PaymentStep from './PaymentStep';
import UserEventList from './UserEventList';
import { getUsersNeedingPartnerForGroup } from './UserEventList/utils';
import WithdrawFromTournament from './WithdrawFromTournament';
import { REGISTER_API, RegistrationFormData } from './types';
import { EXIT_TEXT, EventProps, Steps } from '../types';
import {
  getVairAgeRestrictionMessage,
  getVairGenderMismatchMessage,
  getVairVairifiedRequiredMessage,
} from '../utils';

const IS_AGE_RESTRICTION_ENABLED = false;
const DEFAULT_BANK_ACCOUNT_TITLE = 'One more step!';
const DEFAULT_BANK_ACCOUNT_DESCRIPTION =
  'Connect your payment details to receive earnings seamlessly and securely. This is required before players can register your event.';
const UNAVAILABLE_EVENT_TITLE = 'Register';
const UNAVAILABLE_EVENT_DESCRIPTION = 'This event is still being set up by the organizer';

const PlayerRequestBooking = () => {
  return (
    <div>
      <p className="text-pretty text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        {UNAVAILABLE_EVENT_DESCRIPTION}
      </p>
    </div>
  );
};

const useTournamentPayment = () => {
  const { userId } = useViewer();
  const router = useRouter();
  const [stripeKeys, setStripeKeys] = React.useState<undefined | Response>(undefined);
  const { stripe } = useStripe();

  const fetchSetupIntent = async () => {
    try {
      const response = await getSetupIntent();
      setStripeKeys(response);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    if (router.isReady && userId) {
      fetchSetupIntent();
    }
  }, [userId, router.isReady]);

  return {
    stripeKeys,
    stripe,
    fetchSetupIntent,
  };
};

const useReadyCheck = () => {
  const { get: checkRegisterReady } = useApiGateway<undefined>(REGISTER_API);
  const { get: checkSetupIntentReady } = useApiGateway<undefined>(SETUP_INTENT_API);
  const fetchReadyCheck = async () => {
    try {
      return await Promise.all([checkRegisterReady(), checkSetupIntentReady()]);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return { fetchReadyCheck };
};

const RegisterWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <div className="hidden h-full flex-col overflow-hidden rounded-3xl border border-color-border-input-lightmode shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode lg:flex">
        {children}
      </div>
      <div className="flex flex-col lg:hidden">{children}</div>
    </>
  );
};

const ResgisterExternal = ({ url, eventId }: { url: string; eventId: string }) => {
  const { isSessionLoading, isUserSession } = useViewer();
  const { openSignupModal, ModalLogin, ModalSignup } = useAuthModals();

  const redirectToTournament = () => {
    trackEvent(TOURNAMENT_NAVIGATE_EXTERNAL, { url });
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <div className="flex flex-col p-6">
        <div className="typography-product-heading-mobile text-color-brand-primary">Register</div>
        <div className="typography-product-body my-8 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          Registration for this tournament is hosted on another platform.
        </div>
        <div className="text-center">
          <ButtonText
            onClick={() => {
              trackEvent(TOURNAMENT_CLICK_REGISTER_EXTERNAL, { url });
              if (eventId) {
                trackCtaClick({
                  entityType: 'EVENT',
                  entityId: eventId,
                  source: getEngagementSourceFromUrl(),
                });
              }

              if (isUserSession || isSessionLoading) {
                redirectToTournament();
              } else {
                openSignupModal();
              }
            }}
            className="typography-product-button-label-medium flex w-full items-center justify-center text-center text-color-brand-primary"
          >
            Leave Bounce and register <ArrowRight className="ml-1 h-4 w-4" />
          </ButtonText>
        </div>
      </div>
      <ModalSignup
        isShowLoginLink
        handleSignupSuccess={redirectToTournament}
        ignoreText={EXIT_TEXT}
        ignoreAction={redirectToTournament}
      />
      <ModalLogin isShowSignupLink />
    </>
  );
};

export default function MultiStepForm({
  event,
  isUserOrganizer,
  onGameDayClick,
  onEventDataChanged,
}: EventProps) {
  const router = useRouter();
  const [steps, setSteps] = useState(Steps.AddEvent);
  const [isIgnoreInvitation, setIsIgnoreInvitation] = useState(false);
  const { fetchReadyCheck } = useReadyCheck();
  const experience = getEventExperience(event);
  const [registrationFormData, setRegistrationFormData] = useState<RegistrationFormData>({
    duprId: '',
    birthday: '',
    referredBy: '',
    skillLevelId: '',
    groups: [
      {
        groupId: '',
        hasPartner: experience.hasPartners,
        partnerEmail: '',
        partnerName: '',
      },
    ],
  });
  const { isUserSession, isAnonymousSession, userId } = useViewer();
  const { user, loading: isUserLoading, called: userCalled } = useGetCurrentUser();
  const [
    getUserEventRegistrationLazyQuery,
    { data, loading: isRegistrationLoading, called: isRegistrationCalled },
  ] = useGetUserEventRegistrationLazyQuery();
  const [
    getEventTeamInvitationByIdLazyQuery,
    { data: teamInvitationData, loading: isTeamInvitationLoading, called: isTeamInvitationCalled },
  ] = useGetEventTeamInvitationByIdLazyQuery();
  const [getEventInvitationForRegistrationLazyQuery] =
    useGetEventInvitationForRegistrationLazyQuery();
  const [invitationId, setInvitationId] = useState<string | undefined>(undefined);
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  const staleInviteModal = useModal();
  const isLoadingAuth = !isUserSession && !isAnonymousSession;
  const isLoadingUserDetails = isLoadingAuth || (isUserSession && (isUserLoading || !userCalled));
  const isLoadingTeam =
    router.isReady && !!router.query.team && (!isTeamInvitationCalled || isTeamInvitationLoading);
  const isLoadingGroupsDetails =
    isRegistrationLoading || (isUserSession && !isRegistrationCalled) || isLoadingTeam;
  const { stripeKeys, stripe, fetchSetupIntent } = useTournamentPayment();
  const {
    isOpen: isAdditionalDetailsOpen,
    openModal: openAdditionalDetailsModal,
    closeModal: closeAdditionalDetailsModal,
  } = useModal();

  const [updateUserDefaultCoachPaymentFulfillmentChannel] =
    useUpdateUserDefaultCoachPaymentFulfillmentChannelMutation();

  const {
    isOpen: isBankAccountConnectedModalOpen,
    openModal: openBankAccountConnectedModal,
    closeModal: closeBankAccountConnectedModal,
  } = useModal();

  const { vairUser, vairUserLoading } = useVairPlayerSearch();

  // Fetch VAIR data for other registrants on a VAIR event
  const { post: fetchRegistrantVairSearch } = useApiGateway<
    VairPlayersPayload,
    VairPlayerSearchResponse
  >(VAIR_PLAYER_SEARCH_API);
  const [registrantVairMap, setRegistrantVairMap] = useState<Record<string, VairUser>>({});

  const [vairAgeModalContent, setVairAgeModalContent] = useState<{
    title: string;
    message: string;
  }>({ title: '', message: '' });
  const [vairVairifiedModalContent, setVairVairifiedModalContent] = useState<{
    title: string;
    message: string;
  }>({ title: '', message: '' });
  const [genderModalContent, setGenderModalContent] = useState<{
    title: string;
    message: string;
  }>({ title: '', message: '' });
  const [hasDismissedAgeAlert, setHasDismissedAgeAlert] = useState(false);
  const [hasDismissedVairifiedAlert, setHasDismissedVairifiedAlert] = useState(false);
  const [hasDismissedGenderMismatchAlert, setHasDismissedGenderMismatchAlert] = useState(false);
  const {
    isOpen: isVairAgeModalOpen,
    openModal: openVairAgeModal,
    closeModal: closeVairAgeModal,
  } = useModal();
  const {
    isOpen: isVairVairifiedModalOpen,
    openModal: openVairVairifiedModal,
    closeModal: closeVairVairifiedModal,
  } = useModal();
  const {
    isOpen: isGenderMismatchModalOpen,
    openModal: openGenderMismatchModal,
    closeModal: closeGenderMismatchModal,
  } = useModal();
  const { isConnected, openVairConnectionModal, VairConnectionModal, VairConnectionStatusModal } =
    useVairConnection();

  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [paymentFulfillmentChannel, setPaymentFulfillmentChannel] = useState(
    PaymentFulfillmentChannelsEnum.OnPlatform,
  );
  const [tournamentPaymentChoice, setTournamentPaymentChoice] =
    useState<TournamentPaymentChoice | null>(null);

  const isVairRoundRobin = event.groups.some(
    (group) => group.format === EventGroupFormatsEnum.VairRoundRobin,
  );
  const isVairRequired = !!event.isVairRequired;
  const isVairEvent = isVairRoundRobin || isVairRequired;

  const selectedGroupIds = new Set(registrationFormData.groups.map((g) => g.groupId));
  const groupsRequiringVairified = event.groups.filter(
    (g) => selectedGroupIds.has(g.id) && g.isVairVairifiedRequired,
  );
  const isVairVairifiedRequired = groupsRequiringVairified.length > 0;

  const hasEventAgeRestriction = event.groups.some((g) => g.isAgeRestricted);

  // CTA-click engagement signal: fired when the user commits to registering by
  // clicking the primary button on the first (AddEvent) step.
  const trackRegistrationCtaClick = () => {
    if (event.id) {
      trackCtaClick({
        entityType: 'EVENT',
        entityId: event.id,
        source: getEngagementSourceFromUrl(),
      });
    }
  };

  const handleNextWithVairCheck = (nextStep: () => void) => {
    if (!userId) {
      nextStep();
      return;
    }

    if (isVairEvent && !isConnected) {
      setSteps(Steps.AddEvent);
      openVairConnectionModal('join');
      return;
    }

    if (IS_AGE_RESTRICTION_ENABLED && isVairRequired && hasEventAgeRestriction) {
      const ageMessage = getVairAgeRestrictionMessage(event, vairUser?.age ?? null);
      if (ageMessage && !hasDismissedAgeAlert) {
        setVairAgeModalContent(ageMessage);
        openVairAgeModal();
        return;
      }
    }

    if (isVairVairifiedRequired) {
      const vairifiedMessage = getVairVairifiedRequiredMessage(
        getIsVairified(vairUser),
        groupsRequiringVairified,
      );
      if (vairifiedMessage && !hasDismissedVairifiedAlert) {
        setVairVairifiedModalContent(vairifiedMessage);
        openVairVairifiedModal();
        return;
      }
    }

    nextStep();
  };

  useEffect(() => {
    if (userId && event.id) {
      getUserEventRegistrationLazyQuery({
        variables: {
          userId,
          eventId: event.id,
        },
        pollInterval: 20_000,
      });

      fetchReadyCheck().catch(() => {});
    }
  }, [userId, event.id]);

  // Prefill the SOCIAL skill-level question from the player's saved profile level so
  // returning players only have to confirm it. DO_NOT_PLAY and NO_RESPONSE are stored
  // levels the chip group never offers, so they count as unanswered.
  useEffect(() => {
    if (!user) return;

    const savedSkillLevelId =
      (event.sport === SportsEnum.Pickleball && user.pickleballSkillLevel?.id) ||
      (event.sport === SportsEnum.Tennis && user.tennisSkillLevel?.id) ||
      '';
    const isSelfRateLevel = SELF_RATE_SKILL_LEVELS.some(
      (level) => level.value === savedSkillLevelId,
    );

    if (!isSelfRateLevel) return;

    setRegistrationFormData((prev) =>
      prev.skillLevelId === savedSkillLevelId ? prev : { ...prev, skillLevelId: savedSkillLevelId },
    );
  }, [user, event.sport]);

  // Fetch VAIR data for all other registrants when registration data updates on a VAIR event
  useEffect(() => {
    if (!isVairEvent || !data) return;

    const userIdSet = new Set<string>();
    const addId = (id: string | null | undefined) => {
      if (id && id !== userId) userIdSet.add(id);
    };

    for (const reg of data.eventGroupRegistrations ?? []) {
      for (const team of reg.group.teams ?? []) {
        for (const member of team.members ?? []) addId(member.userId);
      }
      for (const invite of reg.invitations ?? []) addId(invite.invitedUserProfile?.id);
    }

    for (const req of data.receivedPartnerRequests ?? []) addId(req.senderUserId);

    // All event group members — covers the "players needing a partner" list
    for (const group of event.groups ?? []) {
      for (const team of group.teams ?? []) {
        for (const member of team.members ?? []) addId(member.userId);
      }
    }

    const uniqueUserIds = [...userIdSet];
    if (uniqueUserIds.length === 0) return;

    const fetchVairData = async () => {
      const result = await fetchRegistrantVairSearch({
        payload: { userIds: uniqueUserIds },
      });
      setRegistrantVairMap((result.data as VairPlayerSearchResponse) ?? {});
    };

    fetchVairData().catch(() => {});
  }, [data, isVairEvent, userId]);

  useEffect(() => {
    if (steps !== Steps.PaymentForm) {
      setTournamentPaymentChoice(null);
    }
  }, [steps]);

  useEffect(() => {
    if (router.isReady && router.query.invite) {
      const id = router.query.invite as string;
      getEventInvitationForRegistrationLazyQuery({ variables: { id } }).then((resp) => {
        const invitation = resp.data?.eventInvitationsByPk;
        const isValid =
          invitation &&
          !invitation.deletedAt &&
          invitation.status === EventInvitationStatusesEnum.Pending;
        if (isValid) {
          setInvitationId(id);
        } else if (invitation) {
          // Invitation exists but is no longer valid — register solo, show warning
          staleInviteModal.openModal();
        }
      });
    }
    if (router.isReady && router.query.team) {
      setTeamId(router.query.team as string);
      getEventTeamInvitationByIdLazyQuery({
        variables: {
          id: router.query.team as string,
        },
      }).then((resp) => {
        const matchedTeam = resp.data?.eventTeamsByPk;
        if (!matchedTeam?.id) {
          setTeamId(undefined);
        }
      });
    }
  }, [router.isReady, router.query.invite, router.query.team]);

  const existingRegistrations = data;
  const hasPreviousEventRegistration = !!existingRegistrations?.eventRegistrations?.length;
  const hasPreviousGroupRegistration = !!existingRegistrations?.eventGroupRegistrations?.length;
  const isFirstTournamentRegistration =
    !existingRegistrations?.eventRegistrations || !existingRegistrations?.eventRegistrations.length;
  // No coupon here — this is a pre-payment estimate used only for isPrivateWithOutCost.
  // PaymentStep recomputes orderTotal with the coupon once the user applies one.
  const orderTotal = calculateEventOrderTotal({
    event,
    registration: registrationFormData.groups,
    isFirstTournamentRegistration,
  });

  const isPrivateWithOutCost = event?.isQuickCreate || !orderTotal?.total ? true : false;

  const needBankInformation =
    !!user && !event?.hostUserProfile?.stripeMerchantDetailsSubmitted && !isPrivateWithOutCost;

  useEffect(() => {
    if (needBankInformation) {
      openBankAccountConnectedModal();
    }
  }, [needBankInformation]);

  if (event.externalUrl) {
    return (
      <RegisterWrapper>
        <ResgisterExternal url={event.externalUrl} eventId={event.id} />
      </RegisterWrapper>
    );
  }

  if (event.canceledAt) {
    return (
      <RegisterWrapper>
        <Canceled event={event} />
      </RegisterWrapper>
    );
  }

  if (isLoadingUserDetails || isLoadingGroupsDetails) {
    return (
      <RegisterWrapper>
        <LoadingSkeleton />
      </RegisterWrapper>
    );
  }

  const now = Date.now();
  const isPastDeadline =
    !!event.registrationDeadlineDateTime &&
    new Date(event.registrationDeadlineDateTime).getTime() < now;
  const isBeforeOpen =
    !!event.registrationOpenDateTime && new Date(event.registrationOpenDateTime).getTime() > now;
  const isPastClosed =
    !!event.registrationClosedAt && new Date(event.registrationClosedAt).getTime() < now;
  const isAllGamedaysConcluded =
    experience.hasCompetition &&
    event.groups.length > 0 &&
    event.groups.every((group) => {
      const sequences = group.sequences ?? [];
      if (sequences.length === 0) return false;
      const finalSequences = sequences.filter((s) => !s.nextSequenceId);
      return finalSequences.length > 0 && finalSequences.every((s) => s.isSequenceComplete);
    });

  return (
    <>
      {!needBankInformation && (
        <RegisterWrapper>
          {steps === Steps.LoadingSkeleton && <LoadingSkeleton />}
          {steps === Steps.AddEvent &&
            (isPastDeadline || isBeforeOpen || isPastClosed || isAllGamedaysConcluded ? (
              <Closed
                setSteps={setSteps}
                event={event}
                user={user}
                registrations={data}
                handleNext={() => {}}
              />
            ) : hasPreviousEventRegistration || hasPreviousGroupRegistration ? (
              <UserEventList
                setSteps={setSteps}
                event={event}
                user={user}
                registrations={data}
                setRegistrationFormData={setRegistrationFormData}
                name={user?.fullName || ''}
                isNextButtonLoading={vairUserLoading}
                vairUsersMap={
                  isVairEvent
                    ? {
                        ...(userId && vairUser ? { [userId]: vairUser } : {}),
                        ...registrantVairMap,
                      }
                    : undefined
                }
                handleNext={(selectedGroups) => {
                  trackRegistrationCtaClick();
                  const doStep = () => setSteps(Steps.PaymentForm);
                  handleNextWithVairCheck(() => {
                    const groups = selectedGroups ?? registrationFormData.groups;
                    const groupIds = groups
                      .map((g) => g.groupId)
                      .filter((id): id is string => !!id);
                    const genderMsg =
                      groupIds.length && vairUser
                        ? getVairGenderMismatchMessage(event, groupIds, vairUser.gender ?? null)
                        : null;
                    if (genderMsg && !hasDismissedGenderMismatchAlert) {
                      setGenderModalContent(genderMsg);
                      openGenderMismatchModal();
                      return;
                    }
                    doStep();
                  });
                }}
                hasPreviousEventRegistration={hasPreviousEventRegistration}
                hasPreviousGroupRegistration={hasPreviousGroupRegistration}
                isIgnoreInvitation={isIgnoreInvitation}
                setIsIgnoreInvitation={setIsIgnoreInvitation}
                invitationGroupId={teamInvitationData?.eventTeamsByPk?.groupId}
                invitationTeamMembers={teamInvitationData?.eventTeamsByPk?.members.map(
                  (member) => ({
                    groupId: teamInvitationData?.eventTeamsByPk?.groupId,
                    name: member.userProfile?.fullName || '',
                    id: member.id,
                  }),
                )}
                isUserOrganizer={isUserOrganizer}
                onPartnerRequestSent={() => {
                  if (userId && event.id) {
                    getUserEventRegistrationLazyQuery({
                      variables: { userId, eventId: event.id },
                      fetchPolicy: 'network-only',
                    });
                  }
                  onEventDataChanged?.();
                }}
                onGameDayClick={onGameDayClick}
              />
            ) : (
              <AddEvents
                isVairRoundRobin={isVairRoundRobin}
                setSteps={setSteps}
                event={event}
                user={user}
                registrations={data}
                registrationFormData={registrationFormData}
                setRegistrationFormData={setRegistrationFormData}
                isIgnoreInvitation={isIgnoreInvitation}
                setIsIgnoreInvitation={setIsIgnoreInvitation}
                invitationGroupId={teamInvitationData?.eventTeamsByPk?.groupId}
                invitationTeamMembers={teamInvitationData?.eventTeamsByPk?.members.map(
                  (member) => ({
                    groupId: teamInvitationData?.eventTeamsByPk?.groupId,
                    name: member.userProfile?.fullName || '',
                    id: member.id,
                  }),
                )}
                isNextButtonLoading={vairUserLoading}
                handleNext={(selectedGroups) => {
                  trackRegistrationCtaClick();
                  const doStep = () => {
                    if (!userId) {
                      setSteps(Steps.CreateAccount);
                    } else {
                      setSteps(Steps.PaymentForm);
                    }
                  };
                  handleNextWithVairCheck(() => {
                    const groups = selectedGroups ?? registrationFormData.groups;
                    const groupIds = groups
                      .map((g) => g.groupId)
                      .filter((id): id is string => !!id);
                    const genderMsg =
                      groupIds.length && vairUser
                        ? getVairGenderMismatchMessage(event, groupIds, vairUser.gender ?? null)
                        : null;
                    if (genderMsg && !hasDismissedGenderMismatchAlert) {
                      setGenderModalContent(genderMsg);
                      openGenderMismatchModal();
                      return;
                    }
                    doStep();
                  });
                }}
                orderTotal={orderTotal}
                isUserOrganizer={isUserOrganizer}
                onGameDayClick={onGameDayClick}
              />
            ))}
          {steps === Steps.PartnerSetup &&
            (() => {
              const groupsNeedingPartner = registrationFormData.groups
                .filter((rg) => rg.hasPartner === true)
                .map((rg) => {
                  const eventGroup = event.groups.find((g) => g.id === rg.groupId);
                  return eventGroup
                    ? {
                        groupId: eventGroup.id,
                        groupTitle: eventGroup.title || 'Untitled Event',
                        usersNeedingPartner: getUsersNeedingPartnerForGroup(
                          event,
                          eventGroup.id,
                          userId,
                        ),
                      }
                    : null;
                })
                .filter((g): g is NonNullable<typeof g> => g !== null);
              const fullVairMap = isVairEvent
                ? { ...(userId && vairUser ? { [userId]: vairUser } : {}), ...registrantVairMap }
                : undefined;
              const initialPartners: Record<
                string,
                { name: string; userId?: string | null; vairRating?: string | null }
              > = {};
              registrationFormData.groups.forEach((rg) => {
                if (rg.partnerName) {
                  const partnerVair = rg.partnerId ? fullVairMap?.[rg.partnerId] : undefined;
                  const parsed = parseVairSportRating(partnerVair?.sport?.pickleball);
                  initialPartners[rg.groupId] = {
                    name: rg.partnerName,
                    userId: rg.partnerId,
                    vairRating: parsed.formattedValue,
                  };
                } else if (rg.partnerEmail) {
                  initialPartners[rg.groupId] = { name: rg.partnerEmail };
                }
              });
              return (
                <PartnerSetup
                  groups={groupsNeedingPartner}
                  initialSelectedPartners={initialPartners}
                  vairUsersMap={
                    isVairEvent
                      ? {
                          ...(userId && vairUser ? { [userId]: vairUser } : {}),
                          ...registrantVairMap,
                        }
                      : undefined
                  }
                  onBack={() => setSteps(Steps.AddEvent)}
                  onPartnerSelected={(groupId, user) => {
                    setRegistrationFormData((prev) => ({
                      ...prev,
                      groups: prev.groups.map((g) =>
                        g.groupId === groupId
                          ? {
                              ...g,
                              partnerName:
                                user.userProfile?.fullName || user.userProfile?.preferredName || '',
                              partnerId: user.userId ?? undefined,
                              partnerType: 'in-event' as const,
                            }
                          : g,
                      ),
                    }));
                  }}
                  onPartnerInvited={(groupId, email) => {
                    setRegistrationFormData((prev) => ({
                      ...prev,
                      groups: prev.groups.map((g) =>
                        g.groupId === groupId
                          ? { ...g, partnerEmail: email, partnerType: 'invite' as const }
                          : g,
                      ),
                    }));
                  }}
                  onDatabaseUserInvited={(groupId, user) => {
                    setRegistrationFormData((prev) => ({
                      ...prev,
                      groups: prev.groups.map((g) =>
                        g.groupId === groupId
                          ? {
                              ...g,
                              partnerName: user.fullName || user.username || '',
                              partnerId: user.id,
                              partnerType: 'invite' as const,
                            }
                          : g,
                      ),
                    }));
                  }}
                  onNext={() => {
                    handleNextWithVairCheck(() => setSteps(Steps.PaymentForm));
                  }}
                />
              );
            })()}
          {steps === Steps.Withdraw && existingRegistrations && (
            <WithdrawFromTournament
              setSteps={setSteps}
              event={event}
              registrations={existingRegistrations}
              onWithdrawSuccess={() => {
                if (userId && event.id) {
                  getUserEventRegistrationLazyQuery({
                    variables: { userId, eventId: event.id },
                    fetchPolicy: 'network-only',
                  });
                }
                onEventDataChanged?.();
                setSteps(Steps.AddEvent);
              }}
            />
          )}
          {steps === Steps.Login && (
            <Login
              setSteps={setSteps}
              event={event}
              user={user}
              registrations={data}
              handleNext={() => {
                handleNextWithVairCheck(() => {
                  setSteps(Steps.AddEvent);
                });
              }}
            />
          )}
          {steps === Steps.CreateAccount && (
            <CreateAccount
              setSteps={setSteps}
              event={event}
              user={user}
              registrations={data}
              handleNext={() => {
                handleNextWithVairCheck(() => {
                  setSteps(Steps.AddEvent);
                });
              }}
            />
          )}
          {steps === Steps.PaymentForm && (
            <PaymentStep
              event={event}
              isUserOrganizer={isUserOrganizer}
              stripe={stripe}
              stripeKeys={stripeKeys}
              fetchSetupIntent={fetchSetupIntent}
              setSteps={setSteps}
              registrationFormData={registrationFormData}
              orderTotal={orderTotal}
              isFirstTournamentRegistration={isFirstTournamentRegistration}
              tournamentPaymentChoice={tournamentPaymentChoice}
              setTournamentPaymentChoice={setTournamentPaymentChoice}
              isIgnoreInvitation={isIgnoreInvitation}
              invitationId={invitationId}
              teamId={teamId}
              userId={userId}
              isPrivateWithOutCost={isPrivateWithOutCost}
              onEventDataChanged={onEventDataChanged}
            />
          )}
          {steps === Steps.Closed && (
            <Closed
              setSteps={setSteps}
              event={event}
              user={user}
              registrations={data}
              handleNext={() => {}}
            />
          )}
        </RegisterWrapper>
      )}

      {isUserOrganizer && !!user && needBankInformation && (
        <BankAccountPrompt
          onConnectBankAccount={() => {
            setIsPayoutModalOpen(true);
          }}
          heading={DEFAULT_BANK_ACCOUNT_TITLE}
          description={DEFAULT_BANK_ACCOUNT_DESCRIPTION}
          showButton
        />
      )}

      {!isUserOrganizer && !!user && needBankInformation && (
        <BankAccountPrompt
          heading={UNAVAILABLE_EVENT_TITLE}
          description=""
          showButton={false}
          action={<PlayerRequestBooking />}
        />
      )}
      <ModalAdditionalDetails
        isOpen={isAdditionalDetailsOpen}
        handleClose={closeAdditionalDetailsModal}
      />
      {isUserOrganizer && needBankInformation && (
        <Modal
          isOpen={isBankAccountConnectedModalOpen}
          handleClose={closeBankAccountConnectedModal}
        >
          <ButtonText
            onClick={() => closeBankAccountConnectedModal()}
            className="absolute right-4 top-4 z-100 rounded-full p-2"
          >
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
          <BankAccountPrompt
            onConnectBankAccount={() => {
              setIsPayoutModalOpen(true);
            }}
            heading={DEFAULT_BANK_ACCOUNT_TITLE}
            description={DEFAULT_BANK_ACCOUNT_DESCRIPTION}
            showButton
          />
        </Modal>
      )}
      <ModalStripeOnboarding
        isOpen={isPayoutModalOpen}
        navigateTo={ForNavigationType.Event}
        resourceId={event?.id}
        handleClose={(didAddBankInformation) => {
          setIsPayoutModalOpen(false);
          if (userId) {
            if (didAddBankInformation) {
              updateUserDefaultCoachPaymentFulfillmentChannel({
                variables: {
                  id: userId,
                  defaultCoachPaymentFulfillmentChannel: PaymentFulfillmentChannelsEnum.OnPlatform,
                },
              }).catch((error) => Sentry.captureException(error));
              setPaymentFulfillmentChannel(PaymentFulfillmentChannelsEnum.OnPlatform);
            }
          }
        }}
      />
      {VairConnectionModal}
      {VairConnectionStatusModal}
      <ModalVairInfo
        isOpen={isVairAgeModalOpen}
        onClose={closeVairAgeModal}
        onConfirm={() => {
          setHasDismissedAgeAlert(true);
          closeVairAgeModal();
        }}
        title={vairAgeModalContent.title}
        message={vairAgeModalContent.message}
      />
      <ModalVairInfo
        isOpen={isVairVairifiedModalOpen}
        onClose={closeVairVairifiedModal}
        onConfirm={() => {
          setHasDismissedVairifiedAlert(true);
          closeVairVairifiedModal();
        }}
        title={vairVairifiedModalContent.title}
        message={vairVairifiedModalContent.message}
      />
      <ModalVairInfo
        isOpen={isGenderMismatchModalOpen}
        onClose={() => {
          setHasDismissedGenderMismatchAlert(true);
          closeGenderMismatchModal();
        }}
        title={genderModalContent.title}
        message={genderModalContent.message}
      />
      <Modal
        isOpen={staleInviteModal.isOpen}
        handleClose={staleInviteModal.closeModal}
        classNameMaxWidth="max-w-[40rem]"
      >
        <div className="flex flex-col items-center px-6 pb-6 pt-8 text-center">
          <p className="typography-product-heading mb-ds-md text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Partner already paired
          </p>
          <p className="typography-product-body mb-ds-2xl text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Your partner has already been paired with someone else. You can still register and
            invite a new partner, or join as a solo player.
          </p>
          <Button variant="primary" size="md" onClick={() => staleInviteModal.closeModal()}>
            Continue registration
          </Button>
        </div>
      </Modal>
    </>
  );
}
